import React from 'react';
import { FileDown, Copy, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { SkipIdentityToggleRow } from './SkipIdentityToggleRow';
import type { QueryResultSet } from '../../../../types/queryExecution';

type ExportParams = { format: 'csv' | 'json' | 'xlsx' | 'markdown' | 'sql'; scope: 'active' | 'all' };
type CopyFormat = 'csv' | 'markdown' | 'insert' | 'html';

/**
 * Which format families the hosting grid can honour. `sqlFormats: false`
 * (the Azure Storage browser, spec §6.3) removes "Copy INSERT", "Export SQL
 * (INSERT)" and the identity toggle they share; every other item is unchanged.
 * The default keeps `ResultsGrid` and every existing caller byte-for-byte as
 * before.
 */
export type GridCopyExportCapabilities = { sqlFormats: boolean };

const DEFAULT_CAPABILITIES: GridCopyExportCapabilities = { sqlFormats: true };

type GridExportCopyMenuProps = {
  capabilities?: GridCopyExportCapabilities;
  canExport: boolean;
  isWindowed: boolean;
  isExporting: boolean;
  copied: boolean;
  /** True while rows are still populating. Disables copy and export triggers. */
  isLoading: boolean;
  isCopyMenuOpen: boolean;
  setIsCopyMenuOpen: (open: boolean) => void;
  isExportMenuOpen: boolean;
  setIsExportMenuOpen: (open: boolean) => void;
  handleCopy: (format: CopyFormat) => Promise<void>;
  // `unknown` return: these report an outcome for `ui_export_results` (see
  // useExportCopy.handleExport); the menu only fires them.
  handleExport: (params: ExportParams) => Promise<unknown>;
  handleExportAllCsv: () => Promise<unknown>;
  bufferRowCount: number;
  windowedTotalRowCount: number;
  remoteTotalRowCount: number | undefined;
  exportableResultSets: QueryResultSet[];
  /** The export scope's hint beside each format (last UI/UX pass, Codex Z7): "Active" (the SQL result set) unless the
   *  host names its own — the storage browser and queue say "Loaded items" (they have no result sets). */
  activeScopeLabel?: string;
};

/**
 * UI/UX batch C2 R13: the Copy and Export menus are real menus (Radix `DropdownMenu`) — arrow keys move between the
 * items, Home / End / type-ahead work, Enter runs one and the menu closes, Escape returns focus to the button — like
 * every other toolbar menu (the storage More menus, the entity Export menu). They used to be popovers of plain buttons
 * reached with Tab. Shared by the Query Editor results grid and the storage browser / queue toolbars; the Table
 * Explorer toolbar's menus are built the same way (two-grid parity, `TableExplorerToolbar`).
 */
const ITEM_CLASS = 'justify-between';
const SECTION_LABEL_CLASS = 'text-[11px] font-normal text-muted-foreground';
const TRIGGER_CLASS = 'p-1.5 rounded-md transition-colors';
const TRIGGER_ENABLED = 'text-muted-foreground hover:text-foreground hover:bg-background/50';
const TRIGGER_DISABLED = 'text-muted-foreground/40 cursor-not-allowed';

export const GridExportCopyMenu = React.memo(function GridExportCopyMenu({
  canExport, isWindowed, isExporting, copied, isLoading,
  isCopyMenuOpen, setIsCopyMenuOpen,
  isExportMenuOpen, setIsExportMenuOpen,
  handleCopy, handleExport, handleExportAllCsv,
  bufferRowCount = 0, windowedTotalRowCount, remoteTotalRowCount, exportableResultSets,
  capabilities = DEFAULT_CAPABILITIES,
  activeScopeLabel = 'Active',
}: GridExportCopyMenuProps) {
  const sqlFormats = capabilities.sqlFormats;
  const canInteract = !isLoading && (canExport || isWindowed);
  const loadingTooltip = 'Not available while loading';
  const fullRowCount = windowedTotalRowCount ?? remoteTotalRowCount ?? 0;
  const safeBuffer = bufferRowCount ?? 0;
  const bufferHeader = `Buffer ${safeBuffer.toLocaleString()} of ${fullRowCount.toLocaleString()} rows`;
  const fullHeader = `All ${fullRowCount.toLocaleString()} rows`;

  return (
    <>
      <DropdownMenu open={isCopyMenuOpen} onOpenChange={setIsCopyMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`${TRIGGER_CLASS} ${canInteract ? TRIGGER_ENABLED : TRIGGER_DISABLED}`}
            disabled={!canInteract}
            title={isLoading ? loadingTooltip : canInteract ? (isWindowed ? 'Copy buffered rows to clipboard' : 'Copy to clipboard') : 'No data to copy'}
            data-testid="copy-results-button"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="copy-results-menu">
          {isWindowed && (
            <>
              <DropdownMenuLabel className={SECTION_LABEL_CLASS}>{bufferHeader}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {([
            { format: 'csv' as const, label: 'Copy CSV' },
            { format: 'markdown' as const, label: 'Copy Markdown' },
            ...(sqlFormats ? [{ format: 'insert' as const, label: 'Copy INSERT' }] : []),
            { format: 'html' as const, label: 'Copy as HTML' },
          ]).map(({ format, label }) => (
            <DropdownMenuItem
              key={format}
              onSelect={() => { void handleCopy(format); }}
              disabled={!canInteract}
              data-testid={`copy-results-${format}`}
            >
              {label}
            </DropdownMenuItem>
          ))}
          {/* INSERT generation option — persisted, shared with Export SQL. */}
          {sqlFormats && (
            <>
              <DropdownMenuSeparator />
              <SkipIdentityToggleRow testId="copy-skip-identity-toggle" />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Export — its own glyph (N27): the storage browser shows a Download button beside it. */}
      <DropdownMenu open={isExportMenuOpen} onOpenChange={setIsExportMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`${TRIGGER_CLASS} ${canInteract && !isExporting ? TRIGGER_ENABLED : TRIGGER_DISABLED}`}
            disabled={!canInteract || isExporting}
            title={isLoading ? loadingTooltip : canInteract ? 'Export results' : 'No results to export'}
            data-testid="export-results-button"
          >
            <FileDown className={`h-3.5 w-3.5 ${isExporting ? 'animate-pulse' : ''}`} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid="export-results-menu">
          {isWindowed && (
            <>
              <DropdownMenuLabel className={SECTION_LABEL_CLASS}>{fullHeader}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => { void handleExportAllCsv(); }}
                disabled={isExporting}
                data-testid="export-all-csv"
              >
                Export All to CSV
              </DropdownMenuItem>
              <DropdownMenuLabel className={SECTION_LABEL_CLASS}>{bufferHeader}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {/* Excel leads — it's the most-used export target (§3d-2). Keep in
              sync with TableExplorerToolbar's export menu (grid parity). */}
          {([
            { format: 'xlsx' as const, label: 'Export Excel' },
            { format: 'csv' as const, label: 'Export CSV' },
            { format: 'json' as const, label: 'Export JSON' },
            { format: 'markdown' as const, label: 'Export Markdown' },
            ...(sqlFormats ? [{ format: 'sql' as const, label: 'Export SQL (INSERT)' }] : []),
          ]).map(({ format, label }) => (
            <DropdownMenuItem
              key={format}
              className={ITEM_CLASS}
              onSelect={() => { void handleExport({ format, scope: 'active' }); }}
              disabled={(!canExport && !isWindowed) || isExporting}
              data-testid={`export-results-active-${format}`}
            >
              <span>{label}</span>
              {!isWindowed && <span className="text-xs text-muted-foreground">{activeScopeLabel}</span>}
            </DropdownMenuItem>
          ))}
          {!isWindowed && exportableResultSets.length > 1 && (
            <>
              {([
                { format: 'xlsx' as const, label: 'Export Excel' },
                { format: 'json' as const, label: 'Export JSON' },
              ]).map(({ format, label }) => (
                <DropdownMenuItem
                  key={`all-${format}`}
                  className={ITEM_CLASS}
                  onSelect={() => { void handleExport({ format, scope: 'all' }); }}
                  disabled={!canExport || isExporting}
                  data-testid={`export-results-all-${format}`}
                >
                  <span>{label}</span>
                  <span className="text-xs text-muted-foreground">All result sets</span>
                </DropdownMenuItem>
              ))}
            </>
          )}
          {/* Same persisted INSERT option as the copy menu (Export SQL). */}
          {sqlFormats && (
            <>
              <DropdownMenuSeparator />
              <SkipIdentityToggleRow testId="export-skip-identity-toggle" />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
});
